import { getTags, getTemplate, getTemplates, useTemplate } from '@/lib/api/templateApi'
import type { RootState } from '@/redux/store'
import { Usage } from '@/typings/domain'
import type {
  GetTemplateArgs,
  PaginationResult,
  Template,
  TemplateDetail,
  TemplateTagCategory,
  UseTemplateArgs,
  UseTemplateError,
  UseTemplateResult,
} from '@/typings/template'
import { UseTemplateErrorType } from '@/typings/template'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash-es'

export type ShallowTemplate = Template &
  Partial<Omit<TemplateDetail, keyof Template>> & {
    similarTemplateTokens?: string[]
    liked?: boolean
    paid?: boolean
    usage?: Usage
  }

type FilteredTemplateState = {
  tokens: string[]
  loaded: boolean
  page: number
  pageSize: number
  total: number
  hasNextPage: boolean
  isLoading: boolean
}

type TemplateState = {
  filteredTemplateState: FilteredTemplateState

  templateUsageState: {
    isLoading: boolean
    templateToken?: string
    result?: UseTemplateResult
    error?: UseTemplateError
  }

  tagCategories: TemplateTagCategory[]
}

const templateAdapter = createEntityAdapter<ShallowTemplate, string>({
  selectId: template => template.templateToken,
})

const initialState: TemplateState = {
  filteredTemplateState: {
    tokens: [],
    page: 1,
    pageSize: 20,
    hasNextPage: false,
    total: 0,
    isLoading: false,
    loaded: false,
  },
  templateUsageState: { isLoading: false },
  tagCategories: [],
}

const initialFilteredTemplateState: FilteredTemplateState = {
  tokens: [],
  page: 1,
  pageSize: 20,
  hasNextPage: false,
  total: 0,
  isLoading: false,
  loaded: false,
}

const templateSlice = createSlice({
  initialState: templateAdapter.getInitialState(initialState),
  name: 'template',
  reducers: {
    setInitialTemplatesResult: (state, action: PayloadAction<PaginationResult<Template>>) => {
      const { payload } = action
      templateAdapter.upsertMany(state, payload.list)
      state.filteredTemplateState = {
        tokens: payload.list.map(template => template.templateToken),
        page: payload.pageNum,
        pageSize: payload.pageSize,
        hasNextPage: payload.hasNextPage,
        total: payload.total,
        isLoading: false,
        loaded: true,
      }
    },
    setTemplate: templateAdapter.setOne,
    resetUseTemplateState: state => {
      state.templateUsageState.isLoading = false
      state.templateUsageState.templateToken = undefined
      state.templateUsageState.result = undefined
      state.templateUsageState.error = undefined
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTemplatesThunk.pending, state => {
        templateAdapter.removeAll(state)
        state.filteredTemplateState = { ...cloneDeep(initialFilteredTemplateState), isLoading: true }
      })
      .addCase(fetchTemplatesThunk.rejected, state => {
        state.filteredTemplateState.isLoading = false
      })
      .addCase(fetchMoreTemplatesThunk.pending, state => {
        state.filteredTemplateState.isLoading = true
      })
      .addCase(fetchMoreTemplatesThunk.fulfilled, (state, action) => {
        templateAdapter.upsertMany(state, action.payload.list)
        const filteredTemplates = state.filteredTemplateState
        filteredTemplates.tokens.push(...action.payload.list.map(template => template.templateToken))
        filteredTemplates.page = action.payload.pageNum
        filteredTemplates.pageSize = action.payload.pageSize
        filteredTemplates.hasNextPage = action.payload.hasNextPage
        filteredTemplates.total = action.payload.total
        filteredTemplates.isLoading = false
        filteredTemplates.loaded = true
      })
      .addCase(fetchMoreTemplatesThunk.rejected, state => {
        state.filteredTemplateState.isLoading = false
      })
      .addCase(fetchTemplateTagCategoriesThunk.fulfilled, (state, action) => {
        state.tagCategories = action.payload
      })
      .addCase(fetchTemplateDetailThunk.fulfilled, (state, action) => {
        templateAdapter.upsertOne(state, action.payload)
      })
      .addCase(createTemplateUsageThunk.pending, (state, action) => {
        state.templateUsageState = { isLoading: true, templateToken: action.meta.arg.templateToken }
      })
      .addCase(createTemplateUsageThunk.fulfilled, (state, action) => {
        state.templateUsageState.isLoading = false
        state.templateUsageState.result = action.payload
      })
      .addCase(createTemplateUsageThunk.rejected, (state, action) => {
        state.templateUsageState.isLoading = false
        state.templateUsageState.error = action.payload
      })
  },
})

export type FetchTemplatesPayload = Omit<GetTemplateArgs, 'page' | 'size'>

export const fetchTemplatesThunk = createAsyncThunk<PaginationResult<Template>, GetTemplateArgs, { state: RootState }>(
  'template/fetchTemplates',
  async (args, thunkAPI) => {
    const res = await getTemplates(args)
    if (res.data) {
      thunkAPI.dispatch(setInitialTemplatesResult(res.data))
      return res.data
    } else {
      return thunkAPI.rejectWithValue(res.error)
    }
  }
)

export const fetchMoreTemplatesThunk = createAsyncThunk<
  PaginationResult<Template>,
  FetchTemplatesPayload,
  { state: RootState }
>('template/fetchMoreTemplates', async (args, thunkAPI) => {
  const { page, pageSize } = thunkAPI.getState().templates.filteredTemplateState
  const res = await getTemplates({
    ...args,
    page: page + 1,
    size: pageSize ?? initialFilteredTemplateState.pageSize,
  })
  return res.data ?? thunkAPI.rejectWithValue(res.error)
})

export const fetchTemplateTagCategoriesThunk = createAsyncThunk<TemplateTagCategory[]>(
  'template/fetchTagCategories',
  async (_, thunkAPI) => {
    const res = await getTags()
    return res.data ?? thunkAPI.rejectWithValue(res.error)
  }
)

type FetchTemplateDetailPayload = { templateToken: string }

export const fetchTemplateDetailThunk = createAsyncThunk<TemplateDetail, FetchTemplateDetailPayload>(
  'template/fetchTemplateDetail',
  async ({ templateToken }, thunkAPI) => {
    const res = await getTemplate(templateToken)
    return res.data ?? thunkAPI.rejectWithValue(res.error)
  }
)

export const createTemplateUsageThunk = createAsyncThunk<
  UseTemplateResult,
  UseTemplateArgs,
  { rejectValue: UseTemplateError | undefined }
>('template/useTemplate', async (args, thunkAPI) => {
  const res = await useTemplate(args)
  if (!res.data) return thunkAPI.rejectWithValue(res.error?.data)

  if (!res.data.form_token) {
    return thunkAPI.rejectWithValue({
      error: UseTemplateErrorType.TEMPLATE_PURCHASE_REQUIRED,
      template_url: res.data.template_url,
    })
  }

  return res.data
})

export const { setInitialTemplatesResult, setTemplate, resetUseTemplateState } = templateSlice.actions

export const templateSelectors = templateAdapter.getSelectors((state: RootState) => state.templates)

export default templateSlice.reducer
